import {
  ART_PAL,
  body,
  edge,
  fade,
  gloss,
  n,
  rng,
  shade,
  shadowInk,
  star,
  tint,
  warm,
  type ArtPal,
  type ArtTheme,
} from './covers/kit';
import { rasterArt } from './raster';

/*
 * Firecrackers (鞭炮) game art in the "premium casual" style of the covers: a closed red envelope (a plain gold
 * pattern and a round clasp — no 福, no glyphs), the same envelope opened, a gold ingot (元宝), a popped festive
 * firecracker and a burst of sparks. The firecracker is the losing pick, so it is drawn festive and neutral —
 * no skulls, no smoke clouds, nothing mocking. No text, no characters (captions come from i18n only); no clocks,
 * no green headwear, no white-and-black mourning pairs (docs/art-prompts.md).
 * Every builder takes the palette and a defs-id prefix; placeholders until the final graphics.
 */
export type FcArtName = 'envelope' | 'open' | 'ingot' | 'cracker' | 'sparks';

/* ---------------- shared shapes (the cover uses them too) ---------------- */

/** Defs of the gold ingot: `${p}ig`. */
export function fcIngotDefs(P: ArtPal, p: string): string {
  return body(`${p}ig`, warm(P), 0.5, 0.3, 0.5, 1);
}

/** Gold ingot (元宝) centred on (0, 0), ~40 × 22: a boat with upturned ends and a dome (defs: `fcIngotDefs`). */
export function fcIngot(P: ArtPal, p: string): string {
  const e = edge(P.gold, P);
  return (
    `<ellipse cx="0" cy="-5" rx="10" ry="8" fill="url(#${p}ig)" stroke="${e}" stroke-width="1.5"/>` +
    `<path d="M-20 -7Q-11 -1 0 -1Q11 -1 20 -7Q19 7 0 10Q-19 7 -20 -7Z" fill="url(#${p}ig)" stroke="${e}" stroke-width="1.5" stroke-linejoin="round"/>` +
    `<path d="M-15 -3Q0 3 15 -3" fill="none" stroke="${tint(warm(P), 0.45)}" stroke-opacity=".7" stroke-width="1" stroke-linecap="round"/>` +
    gloss(-4, -9, 4, 2, -25, 0.7)
  );
}

/* ---------------- builders of the game pieces (own boxes) ---------------- */

interface Piece {
  w: number;
  h: number;
  b(P: ArtPal, p: string): string;
}

/** Envelope box: 64 × 80, the body x 6…58, y 10…74. */
const EX0 = 6;
const EX1 = 58;
const EY0 = 10;
const EY1 = 74;

/** Closed red envelope: gold inset border, a folded flap with a round gold clasp, a row of gold diamonds. */
function envelopePiece(P: ArtPal, p: string): string {
  const red = P.red;
  const er = edge(red, P);
  const gold = warm(P);
  const cx = (EX0 + EX1) / 2;
  let lattice = '';
  for (let i = 0; i < 4; i++) {
    const x = 17 + i * 10;
    lattice += `M${x} 56l3 3l-3 3l-3 -3Z`;
  }
  return (
    `<defs>${body(`${p}ev`, red, 0.3, 0.35, 0.6, 1)}${fcIngotDefs(P, p)}${fade(`${p}sh`, shadowInk(P), P.dark ? 0.5 : 0.18)}</defs>` +
    `<ellipse cx="${cx}" cy="76" rx="26" ry="3.5" fill="url(#${p}sh)"/>` +
    `<rect x="${EX0}" y="${EY0}" width="${EX1 - EX0}" height="${EY1 - EY0}" rx="7" fill="url(#${p}ev)" stroke="${er}" stroke-width="2"/>` +
    `<rect x="${EX0 + 5}" y="${EY0 + 5}" width="${EX1 - EX0 - 10}" height="${EY1 - EY0 - 10}" rx="4" fill="none" stroke="${gold}" stroke-opacity=".6" stroke-width="1.5"/>` +
    `<path d="M${EX0 + 1} ${EY0 + 4}Q${EX0 + 2} ${EY0} ${EX0 + 7} ${EY0}H${EX1 - 7}Q${EX1 - 2} ${EY0} ${EX1 - 1} ${EY0 + 4}L${cx} ${EY0 + 26}Z" fill="${shade(red, 0.1)}" stroke="${er}" stroke-width="1.5" stroke-linejoin="round"/>` +
    `<path d="${lattice}" fill="${gold}" fill-opacity=".75"/>` +
    `<circle cx="${cx}" cy="${EY0 + 26}" r="7" fill="url(#${p}ig)" stroke="${edge(P.gold, P)}" stroke-width="1.5"/>` +
    `<path d="M${cx} ${EY0 + 22}l4 4l-4 4l-4 -4Z" fill="${red}"/>` +
    gloss(EX0 + 10, EY0 + 34, 3, 10, 0, 0.3)
  );
}

/** Opened envelope: the dark pocket with the flap folded up and a soft gold glow inside (the find sits on top). */
function openPiece(P: ArtPal, p: string): string {
  const red = P.red;
  const er = edge(red, P);
  const gold = warm(P);
  const pocket = shade(red, P.dark ? 0.55 : 0.4);
  const top = EY0 + 12;
  const cx = (EX0 + EX1) / 2;
  return (
    `<defs>${body(`${p}ev`, red, 0.3, 0.35, 0.6, 1)}${fade(`${p}gw`, gold, P.dark ? 0.55 : 0.45)}${fade(`${p}sh`, shadowInk(P), P.dark ? 0.5 : 0.18)}</defs>` +
    `<ellipse cx="${cx}" cy="76" rx="26" ry="3.5" fill="url(#${p}sh)"/>` +
    // flap folded up behind the pocket
    `<path d="M${EX0 + 2} ${top + 2}L${cx} ${top - 20}L${EX1 - 2} ${top + 2}Z" fill="${shade(red, 0.2)}" stroke="${er}" stroke-width="1.5" stroke-linejoin="round"/>` +
    `<rect x="${EX0}" y="${top}" width="${EX1 - EX0}" height="${EY1 - top}" rx="7" fill="${pocket}" stroke="${er}" stroke-width="2"/>` +
    `<ellipse cx="${cx}" cy="${top + 20}" rx="24" ry="18" fill="url(#${p}gw)"/>` +
    // front of the envelope: the lower half with the gold border, folded open along the V
    `<path d="M${EX0} ${top + 22}L${cx} ${top + 34}L${EX1} ${top + 22}V${EY1 - 7}Q${EX1} ${EY1} ${EX1 - 7} ${EY1}H${EX0 + 7}Q${EX0} ${EY1} ${EX0} ${EY1 - 7}Z" fill="url(#${p}ev)" stroke="${er}" stroke-width="2" stroke-linejoin="round"/>` +
    `<path d="M${EX0 + 5} ${top + 28}L${cx} ${top + 39}L${EX1 - 5} ${top + 28}" fill="none" stroke="${gold}" stroke-opacity=".6" stroke-width="1.5" stroke-linejoin="round"/>`
  );
}

/** The gold ingot on its own, 80 × 56. */
function ingotPiece(P: ArtPal, p: string): string {
  return (
    `<defs>${fcIngotDefs(P, p)}${fade(`${p}sh`, shadowInk(P), P.dark ? 0.45 : 0.16)}${fade(`${p}gw`, P.dark ? P.gold : '#FFFFFF', P.dark ? 0.35 : 0.7)}</defs>` +
    `<ellipse cx="40" cy="28" rx="40" ry="26" fill="url(#${p}gw)"/>` +
    `<ellipse cx="40" cy="50" rx="26" ry="4" fill="url(#${p}sh)"/>` +
    `<g transform="translate(40 32) scale(1.75)">${fcIngot(P, p)}</g>`
  );
}

/**
 * A popped festive firecracker, 80 × 80: a red paper tube with gold caps and a gold band, the top frayed open into
 * paper curls, a few gold and orange paper bits around. Festive and calm — this is the losing pick, never a mockery.
 */
function crackerPiece(P: ArtPal, p: string): string {
  const red = P.red;
  const er = edge(red, P);
  const gold = warm(P);
  const eg = edge(P.gold, P);
  const tube =
    `<g transform="translate(40 46) rotate(-24)">` +
    `<rect x="-11" y="-24" width="22" height="46" rx="4" fill="url(#${p}ct)" stroke="${er}" stroke-width="2"/>` +
    `<rect x="-12" y="16" width="24" height="8" rx="3" fill="url(#${p}cg)" stroke="${eg}" stroke-width="1.5"/>` +
    `<rect x="-11" y="-4" width="22" height="5" fill="${gold}" fill-opacity=".85"/>` +
    // frayed top: paper curls opening outwards
    `<path d="M-11 -24Q-16 -32 -12 -38Q-8 -32 -5 -26Q-4 -36 1 -40Q3 -32 2 -26Q8 -35 13 -34Q10 -28 11 -24Z" fill="${tint(red, 0.15)}" stroke="${er}" stroke-width="1.5" stroke-linejoin="round"/>` +
    `<path d="M-6 -24Q0 -21 6 -24" fill="none" stroke="${shade(red, 0.35)}" stroke-width="1.5" stroke-linecap="round"/>` +
    gloss(-5, 0, 2.5, 14, 0, 0.35) +
    `</g>`;
  const r = rng(26);
  let bits = '';
  for (let i = 0; i < 7; i++) {
    const a = -Math.PI * (0.15 + (i / 6) * 0.7) - 0.4;
    const d = 26 + r() * 8;
    const x = 30 + Math.cos(a) * d;
    const y = 22 + Math.sin(a) * d * 0.7 + 8;
    const c = i % 3 === 0 ? P.orange : gold;
    bits +=
      i % 2
        ? `<path d="${star(x, y, 3.5 + r() * 2, 1.4, 4)}" fill="${c}"/>`
        : `<rect x="${n(x - 2.5)}" y="${n(y - 1.5)}" width="5" height="3" rx="1" fill="${c}" transform="rotate(${n(r() * 90)} ${n(x)} ${n(y)})"/>`;
  }
  return (
    `<defs>${body(`${p}ct`, red, 0.35, 0.35, 1, 0.2)}${body(`${p}cg`, gold, 0.45, 0.3, 1, 0.2)}${fade(`${p}sh`, shadowInk(P), P.dark ? 0.45 : 0.16)}</defs>` +
    `<ellipse cx="42" cy="74" rx="22" ry="4" fill="url(#${p}sh)"/>` +
    tube +
    bits
  );
}

/** A burst of gold / orange sparks around the centre, 120 × 120 (transparent middle: it sits over a cell). */
function sparksPiece(P: ArtPal, p: string): string {
  const gold = warm(P);
  const r = rng(58);
  let rays = '';
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2 + (r() - 0.5) * 0.4;
    const d0 = 22 + r() * 8;
    const d1 = d0 + 14 + r() * 14;
    const c = i % 3 === 0 ? P.orange : gold;
    rays += `<path d="M${n(60 + Math.cos(a) * d0)} ${n(60 + Math.sin(a) * d0)}L${n(60 + Math.cos(a) * d1)} ${n(60 + Math.sin(a) * d1)}" stroke="${c}" stroke-width="${n(2.5 + r() * 1.5)}" stroke-linecap="round"/>`;
  }
  let dots = '';
  for (let i = 0; i < 8; i++) {
    const a = r() * Math.PI * 2;
    const d = 30 + r() * 26;
    const x = 60 + Math.cos(a) * d;
    const y = 60 + Math.sin(a) * d;
    dots +=
      i % 2
        ? `<path d="${star(x, y, 3 + r() * 3, 1.3, 4)}" fill="${tint(gold, 0.3)}"/>`
        : `<circle cx="${n(x)}" cy="${n(y)}" r="${n(1.5 + r() * 1.5)}" fill="${P.orange}" fill-opacity=".85"/>`;
  }
  return `<defs>${fade(`${p}fl`, P.dark ? P.gold : '#FFFFFF', P.dark ? 0.45 : 0.85)}</defs><circle cx="60" cy="60" r="34" fill="url(#${p}fl)"/>${rays}${dots}`;
}

const PIECES: Record<FcArtName, Piece> = {
  envelope: { w: 64, h: 80, b: envelopePiece },
  open: { w: 64, h: 80, b: openPiece },
  ingot: { w: 80, h: 56, b: ingotPiece },
  cracker: { w: 80, h: 80, b: crackerPiece },
  sparks: { w: 120, h: 120, b: sparksPiece },
};

export function fcArtSize(name: FcArtName): { w: number; h: number } {
  const b = PIECES[name];
  return { w: b.w, h: b.h };
}

/** Standalone SVG of a piece (ids prefixed per piece + theme). */
export function fcArtSvg(name: FcArtName, theme: ArtTheme, prefix = `f${name}${theme[0]}`): string {
  const b = PIECES[name];
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${b.w} ${b.h}" width="${b.w}" height="${b.h}">${b.b(ART_PAL[theme], prefix)}</svg>`;
}

const uris = new Map<string, string>();

/** Raster key of a piece (ADDENDUM names the firecracker `firecracker`). */
const RASTER_KEY = {
  envelope: 'envelope',
  open: 'open',
  ingot: 'ingot',
  cracker: 'firecracker',
  sparks: 'sparks',
} as const;

/**
 * URL of a piece: the raster (`firecrackers/<key>`; the closed and open envelope only as a pair) once
 * `pnpm art:process` has produced it, else the data URI of the vector piece (cached per theme).
 */
export function fcArtUri(name: FcArtName, theme: ArtTheme): string {
  const raster = rasterArt('firecrackers', RASTER_KEY[name]);
  if (raster) return raster;
  const key = name + theme;
  let u = uris.get(key);
  if (!u) {
    u = 'data:image/svg+xml;utf8,' + encodeURIComponent(fcArtSvg(name, theme));
    uris.set(key, u);
  }
  return u;
}

export const FC_ART_NAMES = Object.keys(PIECES) as FcArtName[];
